import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

import { useAuth } from '../../contexts/AuthContext';
import UserManagementTable from './components/UserManagementTable';
import RoleAssignmentModal from './components/RoleAssignmentModal';
import PermissionAuditLog from './components/PermissionAuditLog';
import { supabase } from '../../lib/supabase';
import { adminUserManagementService } from '../../services/adminUserManagementService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

const AdminPanelDashboard = () => {
  const { hasRole, userProfile } = useAuth();
  const isAdmin = hasRole('admin');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterConfirmation, setFilterConfirmation] = useState('all');
  const [players, setPlayers] = useState([]);
  const [confirmationData, setConfirmationData] = useState({
    profileId: '',
    notes: ''
  });
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    scouts: 0,
    clubs: 0,
    players: 0,
    coaches: 0,
    keepers: 0,
    admins: 0,
    unconfirmed: 0
  });

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
      fetchPlayers();
    }
  }, [isAdmin]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('*, assigned_profile:player_master_profiles!user_profiles_assigned_profile_id_fkey(full_name, primary_position)')
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      setUsers(data || []);
      calculateStats(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async () => {
    try {
      const { data, error } = await supabase
        ?.from('player_master_profiles')
        ?.select('id, full_name, primary_position')
        ?.order('full_name', { ascending: true });

      if (error) throw error;
      setPlayers(data || []);
    } catch (error) {
      console.error('Error fetching players:', error);
    }
  };

  const calculateStats = (userData) => {
    const totalUsers = userData?.length || 0;
    const activeUsers = userData?.filter(u => u?.is_active)?.length || 0;
    const scouts = userData?.filter(u => u?.role === 'scout')?.length || 0;
    const clubs = userData?.filter(u => u?.role === 'club')?.length || 0;
    const players = userData?.filter(u => u?.role === 'player')?.length || 0;
    const coaches = userData?.filter(u => u?.role === 'coach')?.length || 0;
    const keepers = userData?.filter(u => u?.role === 'keeper')?.length || 0;
    const admins = userData?.filter(u => u?.role === 'admin')?.length || 0;
    const unconfirmed = userData?.filter(u => 
      ['player', 'coach', 'keeper']?.includes(u?.role) && !u?.is_confirmed
    )?.length || 0;

    setStats({
      totalUsers,
      activeUsers,
      scouts,
      clubs,
      players,
      coaches,
      keepers,
      admins,
      unconfirmed
    });
  };

  const handleRoleChange = (user) => {
    if (!isAdmin) return;
    setSelectedUser(user);
    setIsRoleModalOpen(true);
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    if (!isAdmin) return;
    
    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({ is_active: !currentStatus, updated_at: new Date()?.toISOString() })
        ?.eq('id', userId);

      if (error) throw error;

      await fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Failed to update user status: ' + error?.message);
    }
  };

  const handleRoleSave = async () => {
    setIsRoleModalOpen(false);
    await fetchUsers();
  };

  const handleConfirmUser = (user) => {
    if (!isAdmin) return;
    setSelectedUser(user);
    setConfirmationData({ profileId: '', notes: '' });
    setIsConfirmModalOpen(true);
  };

  const handleConfirmSubmit = async () => {
    if (!selectedUser || !confirmationData?.profileId) {
      alert('Please select a player profile to assign');
      return;
    }

    try {
      await adminUserManagementService?.confirmUser(
        selectedUser?.id,
        confirmationData?.profileId,
        confirmationData?.notes
      );
      await fetchUsers();
      setIsConfirmModalOpen(false);
      setSelectedUser(null);
      setConfirmationData({ profileId: '', notes: '' });
    } catch (error) {
      console.error('Error confirming user:', error);
      alert('Failed to confirm user: ' + error?.message);
    }
  };

  const handleRevokeConfirmation = async (userId) => {
    if (!isAdmin) return;
    
    if (!confirm('Are you sure you want to revoke confirmation for this user?')) {
      return;
    }

    try {
      await adminUserManagementService?.revokeConfirmation(userId);
      await fetchUsers();
    } catch (error) {
      console.error('Error revoking confirmation:', error);
      alert('Failed to revoke confirmation: ' + error?.message);
    }
  };

  const filteredUsers = users?.filter(user => {
    const matchesSearch = 
      user?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesRole = filterRole === 'all' || user?.role === filterRole;
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'active' && user?.is_active) ||
      (filterStatus === 'inactive' && !user?.is_active);
    const matchesConfirmation = filterConfirmation === 'all' ||
                               (filterConfirmation === 'confirmed' && user?.is_confirmed) ||
                               (filterConfirmation === 'unconfirmed' && !user?.is_confirmed);
    
    return matchesSearch && matchesRole && matchesStatus && matchesConfirmation;
  });

  if (!isAdmin) {
    return (
      <>
        <Helmet>
          <title>Access Denied | Prox11Market</title>
        </Helmet>
        <Header />
        <div className="min-h-screen bg-background pt-16 flex items-center justify-center">
          <div className="text-center">
            <Icon name="ShieldAlert" size={64} className="mx-auto mb-4 text-error" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Access Denied</h1>
            <p className="text-muted-foreground">You do not have permission to access this page.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Admin Panel - User Management | Prox11Market</title>
      </Helmet>
      <Header />
      <div className="min-h-screen bg-background pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-foreground font-headline mb-2">
                  Admin Panel - User Management
                </h1>
                <p className="text-muted-foreground">
                  Manage user roles, permissions, and access levels across the platform
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="px-4 py-2 bg-primary/10 rounded-lg">
                  <p className="text-xs text-muted-foreground">Logged in as</p>
                  <p className="text-sm font-semibold text-primary">{userProfile?.full_name}</p>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                <div className="flex items-center justify-between mb-2">
                  <Icon name="Users" size={24} className="text-primary" />
                  <span className="text-2xl md:text-3xl font-bold text-foreground">{stats?.totalUsers}</span>
                </div>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                <div className="flex items-center justify-between mb-2">
                  <Icon name="CheckCircle" size={24} className="text-success" />
                  <span className="text-2xl md:text-3xl font-bold text-foreground">{stats?.activeUsers}</span>
                </div>
                <p className="text-sm text-muted-foreground">Active Users</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                <div className="flex items-center justify-between mb-2">
                  <Icon name="Clock" size={24} className="text-warning" />
                  <span className="text-2xl md:text-3xl font-bold text-foreground">{stats?.unconfirmed}</span>
                </div>
                <p className="text-sm text-muted-foreground">Awaiting Confirmation</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                <div className="flex items-center justify-between mb-2">
                  <Icon name="Shield" size={24} className="text-error" />
                  <span className="text-2xl md:text-3xl font-bold text-foreground">{stats?.admins}</span>
                </div>
                <p className="text-sm text-muted-foreground">Admins</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-card border border-border rounded-lg p-4 text-center">
                <p className="text-xl md:text-2xl font-bold text-foreground mb-1">{stats?.scouts}</p>
                <p className="text-xs md:text-sm text-muted-foreground">Scouts</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4 text-center">
                <p className="text-xl md:text-2xl font-bold text-foreground mb-1">{stats?.clubs}</p>
                <p className="text-xs md:text-sm text-muted-foreground">Clubs</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4 text-center">
                <p className="text-xl md:text-2xl font-bold text-foreground mb-1">{stats?.players}</p>
                <p className="text-xs md:text-sm text-muted-foreground">Players</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4 text-center">
                <p className="text-xl md:text-2xl font-bold text-foreground mb-1">{stats?.coaches}</p>
                <p className="text-xs md:text-sm text-muted-foreground">Coaches</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4 text-center">
                <p className="text-xl md:text-2xl font-bold text-foreground mb-1">{stats?.keepers}</p>
                <p className="text-xs md:text-sm text-muted-foreground">Keepers</p>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="flex-1"
              />
              <Select
                value={filterRole}
                onChange={(e) => setFilterRole(e?.target?.value)}
                options={[
                  { value: 'all', label: 'All Roles' },
                  { value: 'admin', label: 'Admin' },
                  { value: 'scout', label: 'Scout' },
                  { value: 'club', label: 'Club' },
                  { value: 'player', label: 'Player' },
                  { value: 'coach', label: 'Coach' },
                  { value: 'keeper', label: 'Keeper' }
                ]}
                className="w-full sm:w-48"
              />
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e?.target?.value)}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' }
                ]}
                className="w-full sm:w-48"
              />
              <Select
                value={filterConfirmation}
                onChange={(e) => setFilterConfirmation(e?.target?.value)}
                options={[
                  { value: 'all', label: 'All Confirmation' },
                  { value: 'confirmed', label: 'Confirmed' },
                  { value: 'unconfirmed', label: 'Unconfirmed' }
                ]}
                className="w-full sm:w-48"
              />
            </div>
          </div>

          {/* Users Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <UserManagementTable
              users={filteredUsers}
              currentUserId={userProfile?.id}
              onRoleChange={handleRoleChange}
              onStatusToggle={handleStatusToggle}
              onConfirmUser={handleConfirmUser}
              onRevokeConfirmation={handleRevokeConfirmation}
              loading={loading}
            />
          )}

          {/* Audit Log Section */}
          <div className="mt-8">
            <PermissionAuditLog />
          </div>
        </div>
      </div>

      {/* Role Assignment Modal */}
      {isRoleModalOpen && (
        <RoleAssignmentModal
          user={selectedUser}
          onClose={() => setIsRoleModalOpen(false)}
          onSave={handleRoleSave}
        />
      )}

      {/* Confirmation Modal */}
      {isConfirmModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground">Confirm User & Assign Profile</h2>
              <button
                onClick={() => setIsConfirmModalOpen(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <div className="space-y-6">
              <div className="bg-muted/50 rounded-lg p-4">
                <h3 className="font-semibold text-foreground mb-2">User Information</h3>
                <div className="space-y-1 text-sm">
                  <p><span className="text-muted-foreground">Name:</span> <span className="text-foreground font-medium">{selectedUser?.full_name}</span></p>
                  <p><span className="text-muted-foreground">Email:</span> <span className="text-foreground">{selectedUser?.email}</span></p>
                  <p><span className="text-muted-foreground">Role:</span> <span className="text-foreground capitalize">{selectedUser?.role}</span></p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Assign Player Profile *
                </label>
                <Select
                  value={confirmationData?.profileId}
                  onChange={(e) => setConfirmationData({ ...confirmationData, profileId: e?.target?.value })}
                  options={[
                    { value: '', label: 'Select a player profile...' },
                    ...players?.map(p => ({
                      value: p?.id,
                      label: `${p?.full_name} - ${p?.primary_position || 'N/A'}`
                    }))
                  ]}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  This profile will be linked to the user's dashboard
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Confirmation Notes (Optional)
                </label>
                <textarea
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  rows={4}
                  placeholder="Add any notes about this confirmation..."
                  value={confirmationData?.notes}
                  onChange={(e) => setConfirmationData({ ...confirmationData, notes: e?.target?.value })}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setIsConfirmModalOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmSubmit}
                  disabled={!confirmationData?.profileId}
                  iconName="CheckCircle"
                  className="flex-1"
                >
                  Confirm User
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminPanelDashboard;